import { useRef, useCallback } from 'preact/hooks';

/**
 * useSwipeDownToClose — reusable swipe-down-to-dismiss for bottom-sheet drawers
 * (unified-drawer.md P2). Models the proven BottomComposer handle gesture (R-005):
 * record the touch start-Y, and dismiss when the drag down exceeds `threshold`
 * (default 56px). Returns touch handlers to spread onto the sheet's drag handle /
 * header strip so the whole top strip is the swipe target.
 *
 * Pair with the state-removal-before-history rule (the `closeLogbook` gotcha) inside
 * the caller's `onClose` for any overlay that also calls `history.back()`.
 *
 * Usage:
 *   const swipe = useSwipeDownToClose(onClose);
 *   <div class="drawer-grab" {...swipe} aria-hidden="true" />
 */
export function useSwipeDownToClose(
  onClose: () => void,
  opts?: { threshold?: number },
): { onTouchStart: (e: TouchEvent) => void; onTouchEnd: (e: TouchEvent) => void } {
  const threshold = opts?.threshold ?? 56;
  const startYRef = useRef<number | null>(null);

  const onTouchStart = useCallback((e: TouchEvent) => {
    startYRef.current = e.touches[0]?.clientY ?? null;
  }, []);

  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      const startY = startYRef.current;
      startYRef.current = null;
      if (startY == null) return;
      const endY = e.changedTouches[0]?.clientY ?? startY;
      if (endY - startY > threshold) onClose();
    },
    [onClose, threshold],
  );

  return { onTouchStart, onTouchEnd };
}
